import { Menu, MenuProps } from '@mui/material';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useOverlayContainer } from '../OverlayLayer';

export type MenuPopperProps = React.PropsWithChildren<{
  anchorRef: React.RefObject<HTMLElement | null>;
}> &
  MenuProps;

export function MenuPopper({
  anchorRef,
  children,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  marginThreshold = 16,
  open,
  TransitionProps,
  ...menuProps
}: MenuPopperProps) {
  const container = useOverlayContainer();
  // MUI moves an overflowing menu inside the viewport, but does not keep its
  // edge aligned with the trigger. Prefer left-edge alignment, then right-edge
  // alignment if it fits. Let MUI adjust the position when neither fits.
  const paperRef = useRef<HTMLElement | null>(null);
  const [alignToEnd, setAlignToEnd] = useState(false);
  // Only apply the automatic flip when both origins request left alignment.
  const usesStartAlignment =
    anchorOrigin.horizontal === 'left' && transformOrigin.horizontal === 'left';

  const updateHorizontalAlignment = useCallback(
    (paper = paperRef.current) => {
      const anchor = anchorRef.current;
      if (!usesStartAlignment || !anchor || !paper) {
        setAlignToEnd(false);
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const menuWidth = paper.getBoundingClientRect().width;
      const viewportWidth = anchor.ownerDocument.defaultView?.innerWidth;
      if (!viewportWidth) return;

      const viewportMargin = marginThreshold ?? 0;
      const startOverflows =
        anchorRect.left + menuWidth > viewportWidth - viewportMargin;
      const endFits = anchorRect.right - menuWidth >= viewportMargin;
      setAlignToEnd(startOverflows && endFits);
    },
    [anchorRef, marginThreshold, usesStartAlignment]
  );

  // Check content changes before paint and viewport changes while open.
  useLayoutEffect(() => {
    if (open) updateHorizontalAlignment();
  }, [children, open, updateHorizontalAlignment]);

  useEffect(() => {
    if (!open) {
      setAlignToEnd(false);
      return;
    }

    const window = anchorRef.current?.ownerDocument.defaultView;
    if (!window) return;

    const handleResize = () => updateHorizontalAlignment();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [anchorRef, open, updateHorizontalAlignment]);

  const horizontalOrigin = alignToEnd ? 'right' : anchorOrigin.horizontal;

  return (
    <Menu
      container={container}
      anchorEl={anchorRef.current}
      anchorOrigin={{ ...anchorOrigin, horizontal: horizontalOrigin }}
      transformOrigin={{ ...transformOrigin, horizontal: horizontalOrigin }}
      marginThreshold={marginThreshold}
      open={open}
      TransitionProps={{
        ...TransitionProps,
        onEntering: (element, isAppearing) => {
          // Capture the menu element when it enters, after it mounts, so the
          // first alignment check can measure its width, including in a portal.
          paperRef.current = element;
          updateHorizontalAlignment(element);
          TransitionProps?.onEntering?.(element, isAppearing);
        },
      }}
      {...menuProps}
    >
      {children}
    </Menu>
  );
}
